import _ from 'lodash';
import css from 'styled-jsx/css';
import React, { Component } from 'react';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import AsyncCreatableSelect from 'react-select-3/async-creatable';
import { ValueType, OptionsType, ActionMeta, components } from 'react-select-3';
import { QualityTagDialog } from 'react-scale/corp/views/CorpProjectGroupManager/components/ContributorQuality/TrustedTags/QualityTagDialog';
import { Dialog } from '@scale/scaleui-radix';
import { ITagJson } from '@scale/shared/types/tag';
import { isValidTagName } from '@scale/shared/validation';
import Spinner from 'design-system/Spinner';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';

export interface IUserTagsProps extends ITagsProps {
  userId: string;
}

export type SelectOption = ITagJson & { loading?: boolean };

export interface IUserTagsState extends ITagsState {}

interface ITagsProps {
  isDisabled?: boolean;
  tags: ITagJson[];
  shouldShowDialogFeatureFlag?: boolean;
  multiple?: boolean;
  onTagsChanged?: (newValues: ITagJson[]) => void;
}

interface ITagsState {
  tags: SelectOption[];
  pendingTagName: string | null;
  showAddTagDialog: boolean;
  userId: string;
}

const labelSpinnerStyles = css.resolve`
  .spinner-wrapper {
    display: inline-block;
    margin-left: 5px;
  }
  .spinner.sm {
    font-size: 85%;
  }
`;

function LabelSpinner() {
  return (
    <React.Fragment>
      <Spinner className={labelSpinnerStyles.className} size="sm" center={false} />
      {labelSpinnerStyles.styles}
    </React.Fragment>
  );
}

function TagComponent(props: React.PropsWithChildren<{ data: SelectOption }>) {
  const text = props.data._id ? (
    <Link href={`/lookup/${props.data._id}`}>
      <a>{props.children}</a>
    </Link>
  ) : (
    props.children
  );
  return (
    <components.MultiValueLabel {...props}>
      {text}
      {props.data.loading ? <LabelSpinner /> : null}
    </components.MultiValueLabel>
  );
}

// Base Tag Selector component
// Lets the user maintain a list of tags and allows for creation of
// new ones
class TagSelector<T extends ITagsProps, K extends ITagsState> extends Component<T, K> {
  static styles = {
    multiValueLabel: (base: any, state: any) => {
      return state.data.loading ? { ...base, paddingRight: 6 } : base;
    },
    multiValueRemove: (base: any, state: any) => {
      return state.data.loading ? { ...base, display: 'none' } : base;
    },
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  constructor(props: T) {
    super(props);

    // @ts-ignore
    this.state = {
      tags: _.filter(
        props.tags,
        obj =>
          typeof obj === 'object' && typeof obj?._id === 'string' && typeof obj?.name === 'string',
      ),
      pendingTagName: null,
      showAddTagDialog: false,
      userId: undefined,
    };
  }

  fetchTags = async (search: string) => {
    if (!search) {
      return [];
    }
    try {
      const results = await apiFetch(null, 'corp-api/tags', {
        query: { search },
      });

      return results.tags;
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      return [];
    }
  };

  createTag = async (name: string) => {
    try {
      this.setState({ tags: [...this.state.tags, { loading: true, _id: '', name }] });
      const { id } = await apiFetch(null, 'corp-api/tags', {
        method: 'POST',
        body: { name },
      });
      await this.addTag({ _id: id, name });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  };

  addTag = async (tag: SelectOption) => {
    const { multiple = true } = this.props;

    if (multiple) {
      const currentTags = _.cloneDeep(this.state.tags);
      const startIdx = currentTags.findIndex(t => t.name === tag.name);
      if (startIdx === -1) {
        currentTags.push(tag);
      } else {
        currentTags[startIdx] = tag;
      }

      this.setState({ tags: currentTags });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged(currentTags);
      }
    } else {
      // Single select mode - still pass as array, but with only one item
      this.setState({ tags: [tag] });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged([tag]);
      }
    }
  };

  addTagFromDialog = async (tag: ITagJson) => {
    const { multiple = true } = this.props;

    if (multiple) {
      const currentTags = _.cloneDeep(this.state.tags);
      const startIdx = currentTags.findIndex(t => t.name === tag.name);
      if (startIdx === -1) {
        currentTags.push(tag);
      } else {
        currentTags[startIdx] = tag;
      }

      this.setState({ tags: currentTags });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged(currentTags);
      }
    } else {
      // Single select mode - still pass as array, but with only one item
      this.setState({ tags: [tag] });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged([tag]);
      }
    }
  };

  showAddTagDialog = (tag: string) => {
    if (this.props.shouldShowDialogFeatureFlag) {
      this.setState({
        showAddTagDialog: true,
        pendingTagName: tag,
      });
    } else {
      this.createTag(tag);
    }
  };

  removeTag = async (tag: SelectOption) => {
    const { multiple = true } = this.props;

    if (multiple) {
      const filteredTags = this.state.tags.filter(t => t._id !== tag._id);
      this.setState({ tags: filteredTags });

      if (this.props.onTagsChanged) {
        this.props.onTagsChanged(filteredTags);
      }
    } else {
      // Single select mode - clear the selection
      this.setState({ tags: [] });

      if (this.props.onTagsChanged) {
        // For single select clear, pass empty array
        this.props.onTagsChanged([]);
      }
    }
  };

  onChange = async (values: ValueType<SelectOption> | undefined, action: ActionMeta) => {
    const anyAction = action as any; // We don't have typings for each action type yet
    const { multiple = true } = this.props;
    switch (action.action) {
      case 'select-option':
        await this.addTag(multiple ? anyAction.option : values); // In single select mode, the selected value is in 'values', not 'anyAction.option'
        break;
      case 'remove-value':
        await this.removeTag(anyAction.removedValue);
        break;
      case 'clear':
        if (!multiple) {
          // Single select clear
          this.setState({ tags: [] });
          if (this.props.onTagsChanged) {
            this.props.onTagsChanged([]);
          }
        }
        break;
      case 'deselect-option':
      case 'pop-value':
      case 'set-value':
      case 'create-option':
        window.alert(`Unsupported action on UserTags Select: ${action.action}`);
        break;
    }
  };

  isValidNewTag = (
    name: string,
    _selectValue: ValueType<SelectOption>,
    options: OptionsType<SelectOption>,
  ) =>
    !!name &&
    isValidTagName(name) &&
    !options.map(tag => tag.name.toLowerCase()).includes(name.toLowerCase());

  render() {
    return (
      <>
        <AsyncCreatableSelect
          isMulti={this.props.multiple !== false}
          cacheOptions
          isClearable={false}
          backspaceRemovesValue={false}
          components={{ MultiValueLabel: TagComponent }}
          value={this.props.multiple !== false ? this.state.tags : this.state.tags[0] || null}
          loadOptions={this.fetchTags}
          getOptionLabel={(o: SelectOption | { label: string }) =>
            'label' in o ? o.label : o.name
          }
          getOptionValue={o => o._id}
          onCreateOption={this.showAddTagDialog}
          onChange={this.onChange}
          isValidNewOption={this.isValidNewTag}
          styles={TagSelector.styles}
          isDisabled={this.props.isDisabled}
          menuPortalTarget={document.body}
          menuPosition={'absolute'}
        />
        {this.state.showAddTagDialog && (
          <div className="scale-tailwind">
            <Dialog.Root open={this.state.showAddTagDialog}>
              <Dialog.Content>
                <QualityTagDialog
                  currentTag={this.state.pendingTagName || ''}
                  userId={this.state.userId || ''}
                  setShowAddTagModal={showAddTagModal =>
                    this.setState({ showAddTagDialog: showAddTagModal })
                  }
                  onCreateTag={this.addTagFromDialog}
                />
              </Dialog.Content>
            </Dialog.Root>
          </div>
        )}
      </>
    );
  }
}

export default TagSelector;
